import {
    Box,
    Radio,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { KboFlowDebugService } from "services/KboFlowDebugService";
import {
    KboExplorationNode,
    KboFlowRunOutput,
} from "../KboFlowDebug/KboFlowRunDebug";
import { shortId } from "../KboFlowDebug/KboFlowDebugUI";
import {
    getStepDebugNodeOptions,
    StepDebugNodeOption,
} from "./FlowStepDebugNodeOptions";
import { SectionCardHeader } from "./FlowStepDebugUI";

/** Caps the node list so the footer's "continue" button stays on screen. */
const NODE_LIST_MAX_HEIGHT = 320;

function nodeOrigin(node: KboExplorationNode): string {
    return node.parentNodeId
        ? `Child of ${shortId(node.parentNodeId)}`
        : "Root node";
}

/** The one-line finding name shown at the top of this node's selected result. */
function nodeFindingSummary(node: KboExplorationNode): string {
    const selectedFinding = (node.analysis?.selected ?? [])
        .map((selected) => selected.result ?? selected.finding)
        .find(Boolean);
    const firstFinding = (node.analysis?.results ?? [])
        .flatMap((result) => [
            ...(result.results ?? []),
            ...(result.findings ?? []),
        ])
        .find(Boolean);
    const finding = selectedFinding ?? firstFinding;
    return (
        finding?.name?.trim() ||
        finding?.claim?.trim() ||
        "No finding recorded"
    );
}

/** Conversations this node's own analysis covered; falls back to what it started from. */
function nodeConversationCount(node: KboExplorationNode): string {
    const count =
        node.analysis?.analyzedCohortCount ?? node.focus?.startCohortCount;
    return count === undefined ? "—" : count.toLocaleString();
}

/**
 * Whether this node's own analysis selected a finding — i.e. whether select-relevant-findings and
 * should-continue-analysis had something to act on here, as opposed to a dead end. Counts how many
 * of those selections were flagged to explore deeper, since that predicts whether the node has
 * children.
 */
function nodeFindingsSummary(node: KboExplorationNode): string {
    const selected = node.analysis?.selected ?? [];
    if (selected.length === 0) return "None selected";
    const exploredDeeper = selected.filter(
        (finding) => finding.exploreDeeper,
    ).length;
    return exploredDeeper > 0
        ? `${selected.length} selected · ${exploredDeeper} explored deeper`
        : `${selected.length} selected`;
}

function NodeRowSkeleton() {
    return (
        <TableBody>
            {Array.from({ length: 3 }).map((_, index) => (
                <TableRow key={index}>
                    {Array.from({ length: 6 }).map((_cell, cellIndex) => (
                        <TableCell key={cellIndex}>
                            <Skeleton height={30} />
                        </TableCell>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    );
}

/**
 * Lets the user pick which node in the source run's exploration tree "4c · Analyze Node" should
 * run against, instead of silently defaulting to the first root node. Depth is shown as context,
 * not as the selector. Nodes that produced emailed findings appear first because those are the
 * most likely branches an administrator needs to debug.
 */
export function FlowStepDebugNodePicker({
    service,
    sourceRunId,
    sourceRunOutput,
    selectedNodeId,
    debugTarget,
    onSelectNode,
}: {
    service: KboFlowDebugService;
    /** The run to read the tree from. Absent while no run is picked yet. */
    sourceRunId?: string;
    /** Already-loaded output supplied by the Runs-page handoff. */
    sourceRunOutput?: KboFlowRunOutput;
    selectedNodeId?: string;
    /** The result that initiated this contextual debug handoff, if it belongs to this node. */
    debugTarget?: { nodeId?: string; resultTitle?: string };
    onSelectNode: (nodeId: string | undefined) => void;
}) {
    const theme = useTheme();
    const [nodeOptions, setNodeOptions] = useState<StepDebugNodeOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>();

    useEffect(() => {
        if (!sourceRunId) {
            setNodeOptions([]);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError(undefined);

        const applyOutput = (output: KboFlowRunOutput | undefined) => {
            if (cancelled) return;
            const found = output ? getStepDebugNodeOptions(output) : [];
            setNodeOptions(found);
            // The first option is the first emailed finding's node when one was retained.
            if (!found.some(({ node }) => node.nodeId === selectedNodeId)) {
                onSelectNode(found[0]?.node.nodeId);
            }
        };

        if (sourceRunOutput) {
            applyOutput(sourceRunOutput);
            setLoading(false);
            return () => {
                cancelled = true;
            };
        }

        service
            .getRun(sourceRunId)
            .then((run) => {
                const output =
                    typeof run.runOutput === "object" && run.runOutput
                        ? (run.runOutput as KboFlowRunOutput)
                        : undefined;
                applyOutput(output);
            })
            .catch((caught: unknown) => {
                if (cancelled) return;
                setNodeOptions([]);
                setError(
                    caught instanceof Error
                        ? caught.message
                        : "This run's exploration tree could not be loaded.",
                );
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // Re-picks a default only when the run itself changes, not on every selection the user
        // makes afterward.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [service, sourceRunId, sourceRunOutput]);

    return (
        <>
            <SectionCardHeader
                title="4 · Pick the node"
                subtitle="Which node in this run's exploration tree the step should analyze."
            />

            {error ? (
                <Box sx={{ p: 3 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            ) : !loading && nodeOptions.length === 0 ? (
                <Box sx={{ p: 6, textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 700 }}>
                        No nodes recorded
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        This run has no exploration tree to pick a node from.
                    </Typography>
                </Box>
            ) : (
                <TableContainer sx={{ maxHeight: NODE_LIST_MAX_HEIGHT }}>
                    <Table
                        stickyHeader
                        size="small"
                        aria-label="Nodes in this run's exploration tree"
                        sx={{
                            "& thead th": {
                                bgcolor: theme.palette.common.white,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 56 }} />
                                <TableCell sx={{ width: "8%" }}>
                                    Depth
                                </TableCell>
                                <TableCell sx={{ width: "18%" }}>
                                    Origin
                                </TableCell>
                                <TableCell sx={{ width: "12%" }}>
                                    Conversations
                                </TableCell>
                                <TableCell sx={{ width: "18%" }}>
                                    Findings
                                </TableCell>
                                <TableCell>Finding context</TableCell>
                            </TableRow>
                        </TableHead>
                        {loading ? (
                            <NodeRowSkeleton />
                        ) : (
                            <TableBody>
                                {nodeOptions.map(
                                    ({ node, emailFindingTitle }) => {
                                        const isSelected =
                                            node.nodeId === selectedNodeId;
                                        const debugTargetTitle =
                                            isSelected &&
                                            node.nodeId === debugTarget?.nodeId
                                                ? debugTarget?.resultTitle
                                                : undefined;
                                        return (
                                            <TableRow
                                                key={node.nodeId}
                                                hover
                                                selected={isSelected}
                                                onClick={() =>
                                                    onSelectNode(node.nodeId)
                                                }
                                                sx={{ cursor: "pointer" }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Radio
                                                        checked={isSelected}
                                                        value={node.nodeId}
                                                        name="flow-step-debug-node"
                                                        inputProps={{
                                                            "aria-label": `Select node ${shortId(
                                                                node.nodeId,
                                                            )}`,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {node.depth ?? 0}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {nodeOrigin(node)}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: theme.palette
                                                                .gray[500],
                                                            fontFamily:
                                                                "monospace",
                                                        }}
                                                    >
                                                        node{" "}
                                                        {shortId(node.nodeId)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {nodeConversationCount(
                                                            node,
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {nodeFindingsSummary(
                                                            node,
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {debugTargetTitle ||
                                                            emailFindingTitle ||
                                                            nodeFindingSummary(
                                                                node,
                                                            )}
                                                    </Typography>
                                                    {debugTargetTitle ? (
                                                        <Typography
                                                            variant="caption"
                                                            color="primary.main"
                                                            sx={{
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            Debug target
                                                        </Typography>
                                                    ) : emailFindingTitle ? (
                                                        <Typography
                                                            variant="caption"
                                                            color="success.main"
                                                            sx={{
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            Included in email
                                                        </Typography>
                                                    ) : null}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    },
                                )}
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>
            )}
        </>
    );
}
